import { gql, useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { type FormikProps, useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useContext, useEffect } from 'react';
import AssetCount from 'src/components/AssetCount';
import AkomiDialog from 'src/components/dialogs/AkomiDialog';
import AkomiDialogActions from 'src/components/dialogs/AkomiDialogActions';
import SelectWithLabel from 'src/components/SelectWithLabel';
import { ActionDialogContext } from 'src/context/ActionDialogContext';
import { createGallery } from 'src/dynamoQueries/collections';
import { onCreateGallerySuccess, onErrorCreateGallery } from 'src/helpers';
import GalleryTitleInput from '../GalleryTitleInput';

interface MediaGroup {
  id: string;
  name: string;
}

interface GalleryFormValues {
  name: string;
  mediaGroupId: string;
}

interface CreateGalleryMutationData {
  createCollection: {
    collection: {
      id: string;
    };
  };
}

interface CreateGalleryDialogProps {
  isOpen: boolean;
  mediaGroups: MediaGroup[];
  onClose: () => void;
  assetIds: string[];
}

const CreateGalleryDialog = ({
  isOpen,
  mediaGroups,
  onClose,
  assetIds,
}: CreateGalleryDialogProps): React.JSX.Element | null => {
  const navigate = useNavigate();
  const header = 'Create Gallery';
  const { refetch } = useContext(ActionDialogContext);

  const buildCreateGalleryMutation = () => {
    const m = createGallery(true);
    const mutation = jsonToGraphQLQuery(m, { pretty: true });
    return gql(mutation);
  };

  const [
    createGalleryMutation,
    { data: createdGalleryData, loading: createGalleryLoading, error: errorCreatingGallery },
  ] = useMutation<CreateGalleryMutationData>(buildCreateGalleryMutation());

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate, refetch, handleOnClose are stable references
  useEffect(() => {
    if (createdGalleryData?.createCollection) {
      onCreateGallerySuccess({
        navigate,
        galleryId: createdGalleryData?.createCollection?.collection?.id,
      });
      if (refetch) {
        refetch();
      }
      handleOnClose();
    }
  }, [createdGalleryData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleOnClose is stable reference
  useEffect(() => {
    if (errorCreatingGallery) {
      onErrorCreateGallery({ error: errorCreatingGallery });
      handleOnClose();
      return;
    }
  }, [errorCreatingGallery]);

  const handleOnClose = () => {
    onClose();
  };

  const onSave = (values: GalleryFormValues) => {
    createGalleryMutation({
      variables: {
        assetIds,
        mediaGroupId: values.mediaGroupId,
        name: values.name,
      },
    });
  };

  const formik: FormikProps<GalleryFormValues> = useFormik<GalleryFormValues>({
    initialValues: {
      name: '',
      mediaGroupId: '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values);
    },
  });

  if (errorCreatingGallery) {
    return null;
  }

  return (
    <AkomiDialog
      open={isOpen}
      onClose={handleOnClose}
      title={header}
      variant="form"
      maxWidth="xs"
      actions={
        <AkomiDialogActions
          variant="confirm"
          primaryLabel="Create Gallery"
          onPrimary={() => formik.handleSubmit()}
          primaryDisabled={!formik.values.mediaGroupId}
          submitting={createGalleryLoading}
        />
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <GalleryTitleInput formik={formik} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <SelectWithLabel
            items={mediaGroups}
            // biome-ignore lint/suspicious/noExplicitAny: SelectWithLabel expects FormikInstance with mediaGroupId, but GalleryFormValues has more generic typing
            formik={formik as any}
            label="Select a Media Group"
            name="mediaGroupId"
          />
        </div>
        <div>
          <AssetCount assetIds={assetIds} create={true} />
        </div>
      </form>
    </AkomiDialog>
  );
};

export default CreateGalleryDialog;
